import { Features } from '../../../constants/features';
import { LogService } from '../../../services/log-service';
import { UserPermissionService } from '../../../services/user-permission-service';
import Global from '../../../store/global-selectors';
import { supportedInPackingSlips, supportedInPackingSlipsOrderItems } from '../../editor/buttons/field-replacement/messages';
import { getManifestForBackend } from '../navigation-manifest';
import { AUTOMATION_RULES_ID, automationRulesCapability } from './automation-rules-capability';
import { BULK_UPDATE_SHIP_FROM_ID, bulkUpdateShipFromCapability } from './bulk-update/ship-from-capability';
import { BULK_UPDATE_WEIGHT_ID, bulkUpdateWeightCapability } from './bulk-update/weight-capability';
import { BULK_UPDATE_SERVICE_ID, bulkUpdateServiceCapability } from './bulk-update/service-capability';

import { CURRENT_SEARCH_FILTERS_STORAGE_KEY } from '../../../advanced-search/orders/constants/shippy-search-constants';

export const CAPABILITY_IDS = {
  NAVIGATION: 'navigation',
  ORDER_SEARCH: 'order-search',
  AUTOMATION_RULES: AUTOMATION_RULES_ID,
  PACKING_SLIP: 'packing-slip-generator',
  BULK_UPDATE_SHIP_FROM: BULK_UPDATE_SHIP_FROM_ID,
  BULK_UPDATE_WEIGHT: BULK_UPDATE_WEIGHT_ID,
  BULK_UPDATE_SERVICE: BULK_UPDATE_SERVICE_ID,
  SIERRA_HEADLESS: 'sierra-headless',
};

/**
 * @typedef {object} CapabilityParameter
 * @property {string} type - Parameter type (string, object, etc.)
 * @property {boolean} required - Whether the parameter is required
 * @property {string} description - Description of the parameter
 */

/**
 * @typedef {object} Capability
 * @property {string} id - Unique capability identifier
 * @property {string} [name] - Display name (only needed during development, before backend config exists)
 * @property {string} [description] - Description for semantic matching (only needed during development)
 * @property {string} [category] - Category grouping (only needed during development)
 * @property {Record<string, CapabilityParameter>} [parameters] - Capability parameters (only needed during development)
 * @property {string[]} [examples] - Example user prompts (only needed during development)
 * @property {string} [requiredLabsFeature] - Optional Labs feature required for this capability
 * @property {string} [requiredRole] - Optional user role required for this capability (checked via UserPermissionService)
 * @property {() => object} [getAdditionalContext] - Optional function to get additional context
 */

/**
 * @typedef {object} CapabilitiesRegistry
 * @property {string} version - Registry version
 * @property {Record<string, Capability>} capabilities - Available capabilities
 */

/**
 * Capabilities registry defining all available capabilities
 * @type {CapabilitiesRegistry}
 */
export const capabilitiesRegistry = {
  version: '1.0.0',
  capabilities: {
    [CAPABILITY_IDS.NAVIGATION]: {
      id: CAPABILITY_IDS.NAVIGATION,
      getAdditionalContext: (state) => ({
        availableCommands: getManifestForBackend(),
        shipFromLocations: (Global.selectSellerShipFroms(state) ?? []).map((sf) => ({
          shipFromId: sf.shipFromId,
          name: sf.name,
          isDefault: sf.isDefault,
          city: sf.originAddress?.city,
          state: sf.originAddress?.state,
          postalCode: sf.originAddress?.postalCode,
          countryCode: sf.originAddress?.countryCode,
          street: sf.originAddress?.line1,
        })),
        shipFromAddressFields: {
          name: 'Contact name for the ship from location',
          company: 'Company name',
          line1: 'Street address (e.g. 123 Main St)',
          line2: 'Suite, apt, unit (optional)',
          city: 'City',
          state: 'State/province code (e.g. TX, CA)',
          postalCode: 'ZIP/postal code',
          phone: 'Phone number',
        },
      }),
    },
    [CAPABILITY_IDS.ORDER_SEARCH]: {
      id: CAPABILITY_IDS.ORDER_SEARCH,
      getAdditionalContext: (stateSlice) => {
        let currentFilters = null;
        try {
          const stored = sessionStorage.getItem(CURRENT_SEARCH_FILTERS_STORAGE_KEY);
          if (stored) {
            currentFilters = JSON.parse(stored);
          }
        } catch (error) {
          LogService.logError(new Error('Failed to read current search filters from sessionStorage', { cause: error }));
        }

        return {
          currentDateUtc: stateSlice?.currentDateUtc,
          accountCreatedDateUtc: stateSlice?.accountCreatedDateUtc,
          connectedStores: (stateSlice?.seller?.data?.stores ?? [])
            .filter((s) => s.active)
            .map((s) => ({
              storeGuid: s.storeGuid,
              storeName: s.storeName,
              marketplaceName: s.marketplaceName,
            })),
          currentFilters,
        };
      },
    },
    [CAPABILITY_IDS.AUTOMATION_RULES]: automationRulesCapability,
    [CAPABILITY_IDS.PACKING_SLIP]: {
      id: CAPABILITY_IDS.PACKING_SLIP,
      requiredLabsFeature: Features.AiShippyAssistant,
      getAdditionalContext: () => ({
        availableFields: {
          headerFooterFields: supportedInPackingSlips,
          itemFields: supportedInPackingSlipsOrderItems,
        },
        sizes: ['4x6', 'Letter', 'A4', 'A6'],
        fieldSyntax: 'Fields are inserted as [Field Name], e.g., [Order #], [Recipient Name], [Product Name]',
        storeLogoSyntax: '<img style="vertical-align: middle;" src="http://images.shipstation.com/logo/00000" />',
        templateStructure: {
          header: 'Top of packing slip - company info, order number, dates, recipient address',
          itemsHeader: 'Column headers for the items table - typically Item, SKU, Qty, Price',
          items: 'Single item row template - repeated for each product in the order',
          footer: 'Bottom of packing slip - thank you messages, return policies, custom notes',
        },
      }),
    },
    [CAPABILITY_IDS.BULK_UPDATE_SHIP_FROM]: bulkUpdateShipFromCapability,
    [CAPABILITY_IDS.BULK_UPDATE_WEIGHT]: bulkUpdateWeightCapability,
    [CAPABILITY_IDS.BULK_UPDATE_SERVICE]: bulkUpdateServiceCapability,
    [CAPABILITY_IDS.SIERRA_HEADLESS]: {
      id: CAPABILITY_IDS.SIERRA_HEADLESS,
    },
  },
};

/**
 * Filter capabilities based on which Labs features the user has enabled
 * @param {Record<string, Capability>} capabilities - All capabilities from registry
 * @param {string[]} sellerFeatures - Array of enabled seller features (from Redux)
 * @returns {Record<string, Capability>} Filtered capabilities
 */
export const filterCapabilitiesByLabsFeatures = (capabilities, sellerFeatures = []) => {
  return Object.fromEntries(
    Object.entries(capabilities).filter(([, capability]) => {
      if (!capability.requiredLabsFeature) {
        return true;
      }

      return sellerFeatures.includes(capability.requiredLabsFeature);
    }),
  );
};

/**
 * @typedef {object} BackendCapability
 * @property {string} id - Capability ID
 * @property {string} [name] - Display name (only sent for dev capabilities not yet in backend llm-config.json)
 * @property {string} [description] - Description (only sent for dev capabilities not yet in backend llm-config.json)
 * @property {string} [category] - Category (only sent for dev capabilities not yet in backend llm-config.json)
 * @property {Record<string, CapabilityParameter>} [parameters] - Parameters (only sent for dev capabilities)
 * @property {string[]} [examples] - Examples (only sent for dev capabilities)
 * @property {object} [context] - Runtime context from Redux/sessionStorage
 */

/**
 * Filter capabilities based on user roles (using UserPermissionService for admin bypass)
 * @param {Record<string, Capability>} capabilities - Capabilities to filter
 * @param {string[]} userRoles - User's assigned roles
 * @returns {Record<string, Capability>} Filtered capabilities
 */
export const filterCapabilitiesByRole = (capabilities, userRoles = []) => {
  return Object.fromEntries(
    Object.entries(capabilities).filter(([, capability]) => {
      if (!capability.requiredRole) {
        return true;
      }
      return UserPermissionService.userIsInRoleOrHasAdministrationPermission(userRoles, capability.requiredRole);
    }),
  );
};

/**
 * Get capabilities formatted for backend, filtered by Labs features and user roles
 * @param {string[]} sellerFeatures - Array of enabled seller features
 * @param {string[]} userRoles - User's assigned roles
 * @param {Record<string, object>} capabilityContexts - Dynamic contexts from useCapabilityContext.buildAllContexts()
 * @returns {Record<string, BackendCapability>} Backend-ready capabilities
 */
export const getCapabilitiesForBackend = (sellerFeatures = [], userRoles = [], capabilityContexts = {}) => {
  const labsFiltered = filterCapabilitiesByLabsFeatures(capabilitiesRegistry.capabilities, sellerFeatures);
  const filteredCapabilities = filterCapabilitiesByRole(labsFiltered, userRoles);

  /** @type {Record<string, BackendCapability>} */
  const result = {};
  Object.entries(filteredCapabilities).forEach(([id, capability]) => {
    /** @type {BackendCapability} */
    const backendCapability = {
      id: capability.id,
      name: capability.name ?? '',
      description: capability.description ?? '',
      category: capability.category ?? '',
      parameters: capability.parameters ?? {},
      examples: capability.examples ?? [],
    };

    if (capabilityContexts[id]) {
      backendCapability.context = capabilityContexts[id];
    }
    result[id] = backendCapability;
  });
  return result;
};
